/* Results.tsx
   Page de reporting des résultats sur une période sélectionnée.
   Affiche trois tableaux :
     1. Par affaire : heures facturées, non facturées, total, CA HT (via GenericCrudCard sur /projects)
     2. Par facture client : CA HT ventilé par catégorie de prestation (via /reporting/by-customer-invoice)
     3. Par service : agrégats heures et CA HT (via /reporting/by-service)
*/

// Import React
import React, { useState } from 'react';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChartBar,
	faFilter,
	faBriefcase,
	faLayerGroup,
	faFileInvoiceDollar,
} from '@fortawesome/free-solid-svg-icons';

// Import communs et Layout
import { GenericCrudCard, formatLocalDate } from 'common';
import PageHeader from 'layout/PageHeader';
import WidgetSection from 'layout/WidgetSection';
import WidgetHeader from 'layout/WidgetHeader';

// Import projets
import { projectsApi } from 'features/projects/services/projectsApi';
import { ProjectData } from 'features/projects';

// Import reporting
import {
	reportingApi,
	ReportingByServiceRow,
	ReportingByServiceTotals,
	ReportingByCustomerInvoiceRow,
} from 'features/reporting/services/reportingApi';

// --- Utilitaires ---

// Formate un nombre décimal en heures lisibles (ex: 7.5 → "7h30")
const formatHours = (hours?: number | null): string => {
	if (hours == null) return '—';
	const h = Math.floor(hours);
	const m = Math.round((hours - h) * 60);
	return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

// Formate un montant en euros
const formatEur = (amount?: number | null): string => {
	if (amount == null) return '—';
	return new Intl.NumberFormat('fr-FR', {
		style: 'currency',
		currency: 'EUR',
	}).format(amount);
};

// --- Composant principal ---

export const Results: React.FC = () => {
	// Hooks de State — période sélectionnée (valeurs du formulaire, non encore appliquées)
	const today = new Date();
	const firstDayOfYear = formatLocalDate(new Date(today.getFullYear(), 0, 1));
	const todayStr = formatLocalDate(today);

	const [fromDate, setFromDate] = useState<string>(firstDayOfYear);
	const [toDate, setToDate] = useState<string>(todayStr);

	// Période appliquée (déclenchée par le bouton Appliquer)
	const [appliedFrom, setAppliedFrom] = useState<string>(firstDayOfYear);
	const [appliedTo, setAppliedTo] = useState<string>(todayStr);

	// Hooks de State — tableau par service
	const [serviceRows, setServiceRows] = useState<ReportingByServiceRow[]>([]);
	const [serviceTotals, setServiceTotals] =
		useState<ReportingByServiceTotals | null>(null);
	const [loadingServices, setLoadingServices] = useState(false);
	const [serviceError, setServiceError] = useState<string | null>(null);

	// Clé de rechargement pour forcer le re-fetch des GenericCrudCard quand la période change
	const [refreshKey, setRefreshKey] = useState(0);

	// Chargement des données du tableau par service
	const loadByService = (from: string, to: string) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		setLoadingServices(true);
		setServiceError(null);

		reportingApi
			.byService(token, { from, to })
			.then((result) => {
				setServiceRows(result.rows);
				setServiceTotals(result.totals);
			})
			.catch((err) => {
				console.error('Erreur chargement reporting par service', err);
				setServiceError('Erreur lors du chargement des données.');
			})
			.finally(() => setLoadingServices(false));
	};

	// Chargement initial
	React.useEffect(() => {
		loadByService(appliedFrom, appliedTo);
	}, []);

	// Application des filtres : met à jour la période et force le rechargement de tous les tableaux
	const handleApply = () => {
		setAppliedFrom(fromDate);
		setAppliedTo(toDate);
		setRefreshKey((k) => k + 1);
		loadByService(fromDate, toDate);
	};

	// Colonnes du tableau par affaire (GenericCrudCard)
	const projectColumns = [
		// Référence de l'affaire
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (p: ProjectData) => (
				<span className="font-mono text-sm">{p.referenceNumber}</span>
			),
			exportValue: (p: ProjectData) => p.referenceNumber,
			linkUrl: (p: ProjectData) => `/app/projects/${p.id}/edit`,
		},
		// Libellé issu de la dernière version active du devis lié
		{
			label: 'Libellé',
			field: 'latestActiveQuoteVersion.commentIndex',
			render: (p: ProjectData) =>
				p.latestActiveQuoteVersion?.commentIndex ?? '—',
			exportValue: (p: ProjectData) =>
				p.latestActiveQuoteVersion?.commentIndex ?? '',
		},
		// Heures facturées
		{
			label: 'H. facturées',
			field: 'billableHours',
			render: (p: ProjectData) => (
				<span className="text-green-700 font-medium">
					{formatHours(p.billableHours)}
				</span>
			),
			exportValue: (p: ProjectData) => p.billableHours ?? 0,
		},
		// Heures non facturées
		{
			label: 'H. non facturées',
			field: 'nonBillableHours',
			render: (p: ProjectData) => (
				<span className="text-orange-600 font-medium">
					{formatHours(p.nonBillableHours)}
				</span>
			),
			exportValue: (p: ProjectData) => p.nonBillableHours ?? 0,
		},
		// Total heures
		{
			label: 'Total heures',
			field: 'totalHours',
			render: (p: ProjectData) => (
				<span className="font-semibold">{formatHours(p.totalHours)}</span>
			),
			exportValue: (p: ProjectData) => p.totalHours ?? 0,
		},
		// CA HT
		{
			label: 'CA HT',
			field: 'totalExclTax',
			render: (p: ProjectData) => (
				<span className="font-semibold text-indigo-700">
					{formatEur(p.totalExclTax)}
				</span>
			),
			exportValue: (p: ProjectData) => p.totalExclTax ?? 0,
		},
	];

	// Rendu du composant
	return (
		<div className="min-h-screen bg-gray-100 p-6 space-y-6">
			{/* Titre de page */}
			<PageHeader
				icon={faChartBar}
				title="Résultats"
				subtitle="Analyse des heures et du CA sur la période sélectionnée"
			/>

			{/* Widget — Sélecteur de période */}
			<WidgetSection>
				<WidgetHeader icon={faFilter} title="Période analysée" />
				<div className="flex flex-wrap items-end gap-4 mt-2">
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-700">
							Date de début
						</label>
						<input
							type="date"
							value={fromDate}
							onChange={(e) => setFromDate(e.target.value)}
							className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-700">
							Date de fin
						</label>
						<input
							type="date"
							value={toDate}
							onChange={(e) => setToDate(e.target.value)}
							className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
						/>
					</div>
					<button
						onClick={handleApply}
						className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition-colors text-sm font-medium"
					>
						<FontAwesomeIcon icon={faFilter} />
						Appliquer
					</button>
				</div>
			</WidgetSection>

			{/* Tableau 1 — Par affaire (GenericCrudCard paginé) */}
			<div key={refreshKey}>
				<GenericCrudCard<ProjectData>
					queryKey="resultsProjects"
					title="Par affaire"
					icon={faBriefcase}
					fetchData={(token, options) =>
						projectsApi.listPaginated(token, {
							...options,
							filters: {
								...options?.filters,
								// Transmission des filtres de période pour l'agrégat des heures
								activityDate_from: appliedFrom,
								activityDate_to: appliedTo,
							},
						})
					}
					tableColumns={projectColumns}
					emptyMessage="Aucune affaire avec des activités sur cette période."
					exportFileName={`resultats-affaires-${appliedFrom}-${appliedTo}`}
				/>
			</div>

			{/* Tableau 2 — Par facture client
			    Wrapper overflow-x-auto : confine le débordement horizontal du tableau large
			    dans ce seul bloc (scrollbar locale), sans affecter le centrage des autres cards. */}
			<div key={`invoices-${refreshKey}`} className="overflow-x-auto">
				<GenericCrudCard<ReportingByCustomerInvoiceRow>
					queryKey="resultsInvoices"
					title="Par facture client"
					icon={faFileInvoiceDollar}
					fetchData={(token) =>
						reportingApi
							.byCustomerInvoice(token, {
								from: appliedFrom,
								to: appliedTo,
							})
							.then((result) => result.rows)
					}
					tableColumns={[
						{
							label: 'Référence',
							field: 'referenceNumber',
							render: (r) => (
								<span className="font-mono text-sm">{r.referenceNumber}</span>
							),
							exportValue: (r) => r.referenceNumber,
						},
						{
							label: 'Date',
							field: 'invoiceDate',
							render: (r) =>
								new Date(r.invoiceDate).toLocaleDateString('fr-FR'),
							exportValue: (r) => r.invoiceDate,
						},
						{
							label: 'Échéance',
							field: 'dueDate',
							render: (r) =>
								r.dueDate
									? new Date(r.dueDate).toLocaleDateString('fr-FR')
									: '—',
							exportValue: (r) => r.dueDate ?? '',
						},
						{
							label: 'Libellé',
							field: 'label',
							render: (r) => r.label,
							exportValue: (r) => r.label,
						},
						{
							label: 'Affaire',
							field: 'projectReference',
							render: (r) => (
								<span className="font-mono text-sm">
									{r.projectReference ?? '—'}
								</span>
							),
							exportValue: (r) => r.projectReference ?? '',
						},
						// Colonnes de ventilation — field: 'none' car non filtrables côté backend
						{
							label: 'FORFAIT MENSUEL',
							field: 'none',
							render: (r) =>
								r.forfaitMensuel > 0 ? (
									<span className="text-right block">
										{formatEur(r.forfaitMensuel)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.forfaitMensuel,
						},
						{
							label: 'AK OFC',
							field: 'none',
							render: (r) =>
								r.akOfc > 0 ? (
									<span className="text-right block">{formatEur(r.akOfc)}</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.akOfc,
						},
						{
							label: 'FORFAIT',
							field: 'none',
							render: (r) =>
								r.forfait > 0 ? (
									<span className="text-right block">
										{formatEur(r.forfait)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.forfait,
						},
						{
							label: '1/2 FORFAIT',
							field: 'none',
							render: (r) =>
								r.halfForfait > 0 ? (
									<span className="text-right block">
										{formatEur(r.halfForfait)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.halfForfait,
						},
						{
							label: 'SALLE',
							field: 'none',
							render: (r) =>
								r.salle > 0 ? (
									<span className="text-right block">{formatEur(r.salle)}</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.salle,
						},
						{
							label: 'VISITE',
							field: 'none',
							render: (r) =>
								r.visite > 0 ? (
									<span className="text-right block">
										{formatEur(r.visite)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.visite,
						},
						{
							label: 'SEQ. TERRAIN',
							field: 'none',
							render: (r) =>
								r.sequenceTerrain > 0 ? (
									<span className="text-right block">
										{formatEur(r.sequenceTerrain)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.sequenceTerrain,
						},
						{
							label: 'LOC. ÉPROUVETTE',
							field: 'none',
							render: (r) =>
								r.locationEprouvette > 0 ? (
									<span className="text-right block">
										{formatEur(r.locationEprouvette)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.locationEprouvette,
						},
						{
							label: 'AUTRES',
							field: 'none',
							render: (r) =>
								r.autresServices > 0 ? (
									<span className="text-right block">
										{formatEur(r.autresServices)}
									</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.autresServices,
						},
						{
							label: 'CONSO',
							field: 'none',
							render: (r) =>
								r.conso > 0 ? (
									<span className="text-right block">{formatEur(r.conso)}</span>
								) : (
									<span className="text-gray-300 text-right block">—</span>
								),
							exportValue: (r) => r.conso,
						},
						// Colonnes de contrôle — en indigo
						{
							label: 'CA HT FAC',
							field: 'none',
							render: (r) => (
								<span className="text-right block font-semibold text-indigo-700">
									{formatEur(r.caHtFac)}
								</span>
							),
							exportValue: (r) => r.caHtFac,
						},
						{
							label: 'TOTAL HT',
							field: 'none',
							render: (r) => (
								<span className="text-right block font-semibold text-indigo-700">
									{formatEur(r.totalHt)}
								</span>
							),
							exportValue: (r) => r.totalHt,
						},
					]}
					emptyMessage="Aucune facture sur cette période."
					exportFileName={`resultats-factures-${appliedFrom}-${appliedTo}`}
				/>
			</div>

			{/* Widget — Par type de prestation (données agrégées, non paginées) */}
			<WidgetSection>
				<WidgetHeader icon={faLayerGroup} title="Par type de prestation" />

				{loadingServices && (
					<p className="text-gray-500 text-sm">Chargement...</p>
				)}
				{serviceError && <p className="text-red-600 text-sm">{serviceError}</p>}

				{!loadingServices && !serviceError && (
					<div className="overflow-x-auto">
						<table className="min-w-full border border-gray-200 rounded-lg text-sm">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-3 text-left font-medium text-gray-700 border-b">
										Prestation
									</th>
									<th className="px-4 py-3 text-right font-medium text-gray-700 border-b">
										H. facturées
									</th>
									<th className="px-4 py-3 text-right font-medium text-gray-700 border-b">
										H. non facturées
									</th>
									<th className="px-4 py-3 text-right font-medium text-gray-700 border-b">
										Total heures
									</th>
									<th className="px-4 py-3 text-right font-medium text-gray-700 border-b">
										CA HT
									</th>
								</tr>
							</thead>
							<tbody>
								{serviceRows.map((row, index) => (
									<tr
										key={row.serviceId ?? `null-${index}`}
										className="hover:bg-gray-50 transition-colors"
									>
										<td className="px-4 py-3 border-b text-gray-800">
											{row.serviceLabel ?? (
												<span className="italic text-gray-400">
													Sans prestation
												</span>
											)}
										</td>
										<td className="px-4 py-3 border-b text-right text-green-700 font-medium">
											{formatHours(row.billableHours)}
										</td>
										<td className="px-4 py-3 border-b text-right text-orange-600 font-medium">
											{formatHours(row.nonBillableHours)}
										</td>
										<td className="px-4 py-3 border-b text-right font-semibold">
											{formatHours(row.totalHours)}
										</td>
										<td className="px-4 py-3 border-b text-right text-indigo-700 font-semibold">
											{formatEur(row.totalExclTax)}
										</td>
									</tr>
								))}

								{/* Ligne de totaux */}
								{serviceTotals && (
									<tr className="bg-indigo-50 font-bold">
										<td className="px-4 py-3 border-t-2 border-indigo-200 text-indigo-900">
											Total général
										</td>
										<td className="px-4 py-3 border-t-2 border-indigo-200 text-right text-green-700">
											{formatHours(serviceTotals.billableHours)}
										</td>
										<td className="px-4 py-3 border-t-2 border-indigo-200 text-right text-orange-600">
											{formatHours(serviceTotals.nonBillableHours)}
										</td>
										<td className="px-4 py-3 border-t-2 border-indigo-200 text-right">
											{formatHours(serviceTotals.totalHours)}
										</td>
										<td className="px-4 py-3 border-t-2 border-indigo-200 text-right text-indigo-700">
											{formatEur(serviceTotals.totalExclTax)}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				)}
			</WidgetSection>
		</div>
	);
};

export default Results;
